/**
 * Read-only display for the `working_hours_schedule` property field type.
 *
 * Parses the stored JSON via the shared `parseWorkingHours` helper and renders
 * a compact, human-readable summary that groups consecutive days sharing the
 * same hours (e.g. "Mon–Fri  9:00 AM – 5:00 PM" rather than dumping the raw
 * JSON object). Disabled days are listed as "Closed" so operators can confirm
 * a day is intentionally off versus accidentally unset.
 *
 * Used by `PropertyValueLabel.tsx` when `field_type_name === 'working_hours_schedule'`.
 */

import { useMemo } from 'react';

import type { WorkingHoursValue } from '@/lib/crm/utils/workingHours';
import { DAYS_OF_WEEK, parseWorkingHours } from '@/lib/crm/utils/workingHours';
import { type TimeFormat, timeParts, useOrgTimeFormat } from '@/lib/time';

interface WorkingHoursValueLabelProps {
  /** Stored value — either the raw JSON string from the property, or an already-parsed object */
  value: unknown;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg';
  className?: string;
  /** When true, collapse to a single line with "+N more" affordance for tables/inline use */
  truncate?: boolean;
}

const fontSizeClasses = {
  xs: 'text-xs',
  table: 'text-[13px]',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
};

const DAY_LABELS_SHORT: Record<string, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};

/**
 * Convert "09:00" / "17:30" into the org's preferred display, e.g.
 * "9:00 AM" / "5:30 PM" (12h) or "09:00" / "17:30" (24h).
 *
 * Working-hours times are always plain HH:mm strings (no timezone info). We
 * build a local-clock Date from the parsed hour/minute (no TZ conversion
 * happens since both construction and formatting use the browser's local
 * timezone), then format it via the shared `timeParts` options so this
 * matches every other org-format-aware time display.
 */
function formatTime(time: string, format: TimeFormat): string {
  const [hourStr, minStr] = time.split(':');
  const hour = Number(hourStr);
  const min = Number(minStr);
  if (!Number.isFinite(hour) || !Number.isFinite(min)) {
    return time;
  }
  const date = new Date(2000, 0, 1, hour, min);
  return date.toLocaleTimeString('en-US', timeParts(format));
}

interface DisplayGroup {
  startDay: string;
  endDay: string;
  enabled: boolean;
  /** undefined when enabled=false */
  hoursLabel?: string;
}

/**
 * Group consecutive days with identical (enabled, start, end) into a single row.
 *
 * Adjacent off-days collapse into one "Closed" row; adjacent days with the same
 * start/end times collapse into "Mon–Fri 9:00 AM – 5:00 PM".
 */
function groupDays(
  hours: WorkingHoursValue,
  format: TimeFormat
): DisplayGroup[] {
  const groups: DisplayGroup[] = [];
  let current: (DisplayGroup & { sig: string }) | null = null;

  for (const day of DAYS_OF_WEEK) {
    const config = hours[day.key];
    const sig = config.enabled ? `on:${config.start}-${config.end}` : 'off';
    const hoursLabel = config.enabled
      ? `${formatTime(config.start, format)} – ${formatTime(config.end, format)}`
      : undefined;

    if (current && current.sig === sig) {
      current.endDay = DAY_LABELS_SHORT[day.key];
    } else {
      if (current) {
        const { sig: _sig, ...rest } = current;
        groups.push(rest);
      }
      current = {
        startDay: DAY_LABELS_SHORT[day.key],
        endDay: DAY_LABELS_SHORT[day.key],
        enabled: config.enabled,
        hoursLabel,
        sig,
      };
    }
  }
  if (current) {
    const { sig: _sig, ...rest } = current;
    groups.push(rest);
  }
  return groups;
}

function formatGroupDayLabel(group: DisplayGroup): string {
  return group.startDay === group.endDay
    ? group.startDay
    : `${group.startDay}–${group.endDay}`;
}

export const WorkingHoursValueLabel: React.FC<WorkingHoursValueLabelProps> = ({
  value,
  fontSize = 'sm',
  className = '',
  truncate,
}) => {
  const { timeFormat } = useOrgTimeFormat();
  const sizeClass = fontSizeClasses[fontSize];

  const groups = useMemo(() => {
    const parsed = parseWorkingHours(value);
    return groupDays(parsed, timeFormat);
  }, [value, timeFormat]);

  // Truncated (table/inline) mode: show only open groups for compact display.
  // Full mode: show ALL groups so closed days are visible — operators need to
  // confirm a day is intentionally off versus accidentally unset.
  const openGroups = groups.filter(g => g.enabled);

  if (truncate) {
    // Compact table/inline form: first group + "+N more" affordance.
    const truncateGroups = openGroups.length > 0 ? openGroups : groups;
    const first = truncateGroups[0];
    if (!first) {
      return (
        <span
          className={`${sizeClass} ${className}`}
          title='No working hours configured'
        >
          —
        </span>
      );
    }
    const remaining = truncateGroups.length - 1;
    const text = first.enabled
      ? `${formatGroupDayLabel(first)} ${first.hoursLabel}`
      : `${formatGroupDayLabel(first)} Closed`;
    const fullTitle = truncateGroups
      .map(g =>
        g.enabled
          ? `${formatGroupDayLabel(g)} ${g.hoursLabel}`
          : `${formatGroupDayLabel(g)} Closed`
      )
      .join(' · ');
    return (
      <span
        className={`${sizeClass} block truncate ${className}`}
        title={fullTitle}
      >
        {text}
        {remaining > 0 ? ` · +${remaining} more` : ''}
      </span>
    );
  }

  // Full form: stacked rows, one per group — includes closed days.
  return (
    <div className={`${sizeClass} flex flex-col gap-0.5 ${className}`}>
      {groups.map(group => (
        <div
          key={`${group.startDay}-${group.endDay}-${group.enabled}`}
          className='flex items-baseline gap-3'
        >
          <span className='w-20 shrink-0 font-medium text-[var(--color-text-secondary)]'>
            {formatGroupDayLabel(group)}
          </span>
          <span className='text-[var(--color-text-primary)]'>
            {group.enabled ? group.hoursLabel : 'Closed'}
          </span>
        </div>
      ))}
    </div>
  );
};
